/**
 * ContextMenuWrapper
 *
 * Wrapper component for context menu triggers. Accepts and forwards props from
 * parent components — Ant Design's Dropdown injects `onContextMenu` through
 * `cloneElement` — and uses the capture phase to intercept mousedown BEFORE it
 * reaches child components, so a right-click cannot open a Select or DatePicker.
 *
 * A right-click over a text selection yields to the browser instead (SYD-2232):
 * when the target holds a non-collapsed selection — its own, if it is an input or
 * textarea, or the document's, which covers labels, field names and headings — the
 * injected handler is not called and nothing calls `preventDefault`, so the native
 * Cut/Copy/Paste menu shows. The event is left to bubble on purpose —
 * every ancestor wrapper runs the same test and declines too, so no app menu in
 * the chain opens over selected text.
 */

import { FC, ReactNode, useCallback, HTMLAttributes } from 'react';
import { isTextInputElement } from '@/keyboard';

type ContextMenuWrapperProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
};

/**
 * Both arms are needed. `window.getSelection()` does not reflect the internal
 * selection of an input or textarea in most browsers, and `selectionStart` exists
 * on nothing else — so the control arm covers editors and the document arm covers
 * labels, field names and headings. Reading `selectionStart` throws on input types
 * that own no selection (`number`, `email`, `date`), so the same guard
 * `caretAtStart` uses applies: those controls have no selection to protect.
 * The range must intersect the target, so a selection made elsewhere on the page
 * does not suppress the menu on an unrelated element. Total by construction — it
 * never throws out of `handleContextMenu`.
 */
const hasTextSelection = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  try {
    if (isTextInputElement(target)) {
      const { selectionStart, selectionEnd } = target;
      if (selectionStart != null && selectionEnd != null && selectionStart !== selectionEnd) {
        return true;
      }
    }
    const selection = window.getSelection();
    if (selection === null || selection.isCollapsed || selection.rangeCount === 0) return false;
    return selection.getRangeAt(0).intersectsNode(target);
  } catch {
    return false;
  }
};

const ContextMenuWrapper: FC<ContextMenuWrapperProps> = ({
  children,
  onContextMenu,
  onMouseDownCapture,
  ...rest
}) => {
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (hasTextSelection(e.target)) return;
      e.stopPropagation();
      onContextMenu?.(e);
    },
    [onContextMenu]
  );

  const handleMouseDownCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.button === 2) {
        e.stopPropagation();
      }
      onMouseDownCapture?.(e);
    },
    [onMouseDownCapture]
  );

  return (
    <div
      {...rest}
      style={{ display: 'contents', ...rest.style }}
      onContextMenu={handleContextMenu}
      onMouseDownCapture={handleMouseDownCapture}
    >
      {children}
    </div>
  );
};

export { ContextMenuWrapper };
